import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import SEO, { schemas } from '../components/SEO';
import { StatsBar } from '../components/Sections';
import './InnerPage.css';

export default function AboutPage() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); obs.unobserve(e.target); } }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <SEO
        title="About"
        description="Learn how Accugen delivers enterprise-grade AI quality and security evaluation, empowering teams to ship AI systems with confidence and compliance."
        canonical="/about"
        keywords="about Accugen, AI evaluation platform, enterprise AI security, AI quality testing company"
        schema={schemas.breadcrumb([{name:'Home',path:'/'},{name:'About',path:'/about'}])}
      />
      <Header />

      <PageHero
        eyebrow="About Accugen"
        title="A Platform Built<br/>Around <em>You</em>"
        subtitle="Accugen delivers comprehensive AI quality and security evaluation, empowering enterprise teams to ship AI systems with confidence and compliance."
        crumb="About"
      />

      <StatsBar />

      {/* Mission */}
      <section className="ip-section">
        <div className="container">
          <div className="ip-split">
            <div>
              <div className="eyebrow reveal">Our Mission</div>
              <h2 className="section-h reveal d1">Making AI Trustworthy<br/>at <em>Enterprise Scale</em></h2>
              <p className="section-sub reveal d2">
                We built Accugen because enterprise AI systems needed a dedicated evaluation platform — one that goes beyond simple accuracy checks to cover security vulnerabilities, fairness, and regulatory compliance in a single workflow.
              </p>
              <p className="ip-body reveal d3">
                Every organisation deploying AI chatbots faces the same questions: Is it accurate? Is it safe? Is it fair? Does it comply with OWASP LLM Top 10? Accugen answers all of them, automatically, at scale.
              </p>
            </div>
            <div className="ip-visual-card reveal d2">
              <div className="ivc-stat-grid">
                {[
                  { n:'122+', l:'Security Test Cases' },
                  { n:'9/10', l:'OWASP Coverage' },
                  { n:'14',   l:'Attack Categories' },
                  { n:'12',   l:'Bias Dimensions' },
                ].map(s => (
                  <div key={s.l} className="ivc-stat">
                    <div className="ivc-num">{s.n}</div>
                    <div className="ivc-lbl">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How we're different */}
      <section className="ip-section ip-bg-lav">
        <div className="container">
          <div className="eyebrow reveal" style={{textAlign:'center'}}>What Sets Us Apart</div>
          <h2 className="section-h reveal d1" style={{textAlign:'center'}}>Platform Built for<br/><em>Real Enterprise Needs</em></h2>
          <div className="ip-card-grid3 reveal d2">
            {[
              //{ icon:'🎯', title:'Quality-First', text:'Score AI responses for accuracy, hallucination, relevance, and completeness using ROUGE, BLEU, Semantic Similarity, and LLM-as-Judge — all configurable.' },
              { icon:'/icons/about/about_15_quality-first.svg', title:'Quality-First', text:'Score AI responses for accuracy, hallucination, relevance, and completeness using ROUGE, BLEU, Semantic Similarity, and LLM-as-Judge — all configurable.' },
              { icon:'/icons/about/about_16_security-driven.svg', title:'Security-Driven', text:'Test against 14 attack categories, 122+ pre-built cases, and custom scenarios. Every result maps to OWASP LLM Top 10 with severity ratings.' },
              { icon:'/icons/about/about_17_fairness.svg', title:'Fairness-Aware', text:'Multi-prompt evaluation across 12 bias dimensions catches subtle disparities before they reach production and regulatory scrutiny.' },
              { icon:'/icons/about/about_18_cicd-ready.svg', title:'CI/CD-Ready', text:'API-first architecture enables seamless integration into any pipeline. Run regression tests automatically on every model update.' },
              { icon:'/icons/about/about_11_quality-response.svg', title:'Audit-Ready Reports', text:'One-click PDF compliance reports with OWASP mapping, score donuts, collapsible categories, and executive summaries.' },
              { icon:'/icons/about/about_12_parallel-eval.svg', title:'Parallel at Scale', text:'WAL-mode database and concurrent execution evaluate hundreds of prompts simultaneously with real-time progress tracking.' },
            ].map(c => (
              <div key={c.title} className="ip-card">
                <div className="ip-card-icon">
                  {typeof c.icon === 'string' && c.icon.startsWith('/') ? (
                    <img src={c.icon} alt={c.title} />
                  ) : (
                    c.icon
                  )}
                </div>
                <h3>{c.title}</h3>
                <p>{c.text}</p>
              </div>    
            ))}
          </div>
        </div>
      </section>

      {/* Team / company */}
      <section className="ip-section">
        <div className="container">
          <div className="ip-split">
            <div>
              <div className="eyebrow reveal">Our Commitment</div>
              <h2 className="section-h reveal d1">Committed to<br/><em>AI Excellence</em></h2>
              <p className="section-sub reveal d2">Built by a team passionate about making AI safe, fair, and trustworthy for every enterprise — from healthcare to financial services.</p>
              <div className="ip-check-list reveal d3">
                {[
                  'Enterprise-grade security and data privacy',
                  'Continuous coverage of evolving OWASP standards',
                  'Dedicated support for onboarding and configuration',
                  'Regular test library updates for new attack patterns',
                  'Transparent scoring methodology and documentation',
                ].map(t => (
                  <div key={t} className="ip-check-item">
                    <span className="ip-check-icon">✓</span>
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="ip-team-visual reveal d2">
              <div className="itv-big">50+</div>
              <div className="itv-label">Enterprise Clients Worldwide</div>
              <div className="itv-chips">
                {[
                  { icon:'/icons/services/industry_01_healthcare.svg',    label:'Healthcare' },
                  { icon:'/icons/services/industry_02_financial.svg',      label:'FinTech' },
                  { icon:'/icons/services/industry_03_engineering.svg',    label:'Engineering' },
                  { icon:'/icons/services/industry_04_risk-compliance.svg',label:'Compliance' },
                  { icon:'/icons/services/icon_security.svg',              label:'Security' },
                  { icon:'/icons/services/icon_ai-teams.svg',              label:'AI Teams' },
                ].map(c => (
                  <span key={c.label} className="itv-chip">
                    <img src={c.icon} alt={c.label} />
                    {c.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ip-cta-band">
        <div className="container">
          <h2>Ready to Build Trustworthy AI?</h2>
          <p>Start evaluating your GenAI systems with Accugen today.</p>
          <div className="ip-cta-btns">
            <a href="/contact" className="btn-navy" target="_blank" rel="noreferrer">Contact Us →</a>
            <Link to="/contact" className="btn-outline-navy">Get In Touch</Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
